import React, { useState, useEffect, useContext } from 'react';
import { Modal } from 'react-bootstrap';
import Inputs from 'components/inputs/inputs';
import Jwt from 'jsonwebtoken';
import Storage from 'utils/storage';
import Cache from 'utils/cache';
import Config from 'utils/config';
import UserService from 'services/userService';

const initialState = {
  user: null,
  token: null,
  decodedToken: null,
  isAuthenticated: false,
  isMobile: false,
  isPortal: false,
  expires: -1,
  permissions: null,
  hierarchy: null,
};

const loadState = () => {
  try {
    const state = {
      user: Storage.getCompressedData('authState.user') || initialState.user,
      token: Storage.getString('authState.token') || initialState.token,
      decodedToken: (Storage.contains('authState.token') ? Jwt.decode(Storage.getString('authState.token')) : initialState.decodedToken),
      isAuthenticated: (Storage.contains('authState.user') && Storage.contains('authState.token')),
      isMobile: Storage.getBoolean('authState.isMobile') || initialState.isMobile,
      isPortal: Storage.getBoolean('authState.isPortal') || initialState.isPortal,
      expires: Storage.getNumber('authState.expires') || initialState.expires,
      permissions: Storage.getCompressedData('authState.permissions') || initialState.permissions,
      hierarchy: Storage.getCompressedData('authState.hierarchy') || initialState.hierarchy,
    };
    AuthState = state;
    return state;
  }
  catch {
    AuthState = initialState;
    return initialState;
  }
};

const reducer = (state, action) => {
  switch (action.type) {
    case 'LOGIN': {
      const expires = (new Date()).getTime() + ((Config.tokenExpirationMinutes * 60000) - 10000);
      Cache.clear();
      Storage.clear();
      Storage.setCompressedData('authState.user', action.user);
      Storage.setString('authState.token', action.token);
      Storage.setCompressedData('authState.permissions', action.permissions);
      Storage.setCompressedData('authState.hierarchy', action.hierarchy);
      Storage.setBoolean('authState.isMobile', action.mobile);
      Storage.setBoolean('authState.isPortal', action.portal);
      Storage.setNumber('authState.expires', expires);
      return {
        ...state,
        user: action.user,
        token: action.token,
        decodedToken: Jwt.decode(action.token),
        isAuthenticated: true,
        isMobile: action.mobile || false,
        isPortal: action.portal || false,
        hierarchy: action.hierarchy || [],
        expires: expires,
      };
    }
    case 'LOGOUT': {
      Cache.clear();
      Storage.clear();
      Storage.setBoolean('authState.isMobile', state.isMobile);
      return initialState;
    }
    case 'SET_TOKEN': {
      const expires = (new Date()).getTime() + ((Config.tokenExpirationMinutes * 60000) - 10000);
      Storage.setString('authState.token', action.token);
      Storage.setNumber('authState.expires', expires);
      return {
        ...state,
        token: action.token,
        decodedToken: Jwt.decode(action.token),
        expires: expires,
      };
    }
    default:
      return state;
  }
};

//hack to use this store outside of components
export let AuthState = initialState;
const reducerWrapper = (state, action) => {
  AuthState = reducer(state, action);
  return AuthState;
};

let sessionLogoutChecker;
let sessionTimeoutChecker;
let checkerExpiration;

const AuthContext = React.createContext();
export default AuthContext;

export function AuthContextProvider(props) {
  const [authState, dispatch] = React.useReducer(reducerWrapper, loadState());
  const [showExpiringSession, setShowExpiringSession] = useState(false);

  const setAuthLoggedIn = (token, user, permissions, hierarchy, type) => {
    clearTimeoutCheckers();
    setShowExpiringSession(false);
    dispatch({
      type: 'LOGIN',
      token: token,
      user: user,
      permissions: permissions,
      hierarchy: hierarchy,
      mobile: (type === 'mobile'),
      portal: (type === 'portal'),
    });
  };

  const setAuthLoggedOut = () => {
    clearTimeoutCheckers();
    setShowExpiringSession(false);
    dispatch({
      type: 'LOGOUT',
    });
  };

  const setAuthToken = (token) => {
    clearTimeoutCheckers();
    setShowExpiringSession(false);
    dispatch({
      type: 'SET_TOKEN',
      token: token,
    });
  };

  const authIsValid = () => {
    if (!authState || !authState.isAuthenticated || calculateTimeLeft() <= 0) {
      return false;
    }
    else {
      if (sessionLogoutChecker == null || sessionTimeoutChecker == null || checkerExpiration !== authState.expires) {
        resetTimeoutCheckers();
      }
      return true;
    }
  };

  const calculateTimeLeft = () => {
    return authState.expires - (new Date()).getTime();
  };

  const resetTimeoutCheckers = () => {
    clearTimeoutCheckers();

    const expires = authState.expires;
    const now = (new Date()).getTime();
    sessionLogoutChecker = setTimeout(() => {
      if (calculateTimeLeft() <= 1000) {
        setAuthLoggedOut();
      }
      else {
        resetTimeoutCheckers();
      }
    }, expires - now);
    sessionTimeoutChecker = setTimeout(() => {
      if (calculateTimeLeft() > 0) {
        setShowExpiringSession(true);
      }
      else {
        resetTimeoutCheckers();
      }
    }, expires - now - 60000);
    checkerExpiration = expires;
  };

  const clearTimeoutCheckers = () => {
    clearTimeout(sessionLogoutChecker);
    sessionLogoutChecker = null;
    clearTimeout(sessionTimeoutChecker);
    sessionTimeoutChecker = null;
  };

  const onLogoutClicked = () => {
    setAuthLoggedOut();
  };

  const onExtendClicked = async () => {
    setAuthToken(await UserService.API.getNewToken());
  };

  return (
    <AuthContext.Provider value={{ authState, authIsValid, setAuthLoggedIn, setAuthLoggedOut }}>
      {
        (!showExpiringSession) ? null :
          <SessionExpiringPopup onLogoutClicked={onLogoutClicked} onExtendClicked={onExtendClicked} />
      }
      {props.children}
    </AuthContext.Provider>
  );
}

function SessionExpiringPopup(props) {
  const { authState } = useContext(AuthContext);

  const calculateTimeLeft = () => {
    return authState.expires - (new Date()).getTime();
  };

  const [timeLeft, setTimeLeft] = useState(calculateTimeLeft());

  useEffect(() => {
    const countdownInterval = setTimeout(() => {
      setTimeLeft(calculateTimeLeft());
    }, 1000);

    return () => {
      clearInterval(countdownInterval);
    };
  });

  return (
    <Modal show={true} onHide={() => null} centered>
      <div className='modal-header bg-app text-white d-flex justify-content-between align-items-center w-100'>
        <div className='modal-header-left-icon'></div>
        <div className='modal-title'>Session Expiring</div>
        <div className='modal-header-right-icon'></div>
      </div>

      <Modal.Body>
        <div className='py-2'>
          <span>Your session is about to expire. You will be automatically logged out in:</span>
          <div className='text-thicker'>{Math.round(timeLeft / 1000)} seconds</div>
        </div>
      </Modal.Body>

      <Modal.Footer>
        <Inputs.Button variant='hover-danger' onClick={props.onLogoutClicked} iconClass='icons8-logout-rounded-up' label='Logout Now' />
        <Inputs.Button variant='app' onClick={props.onExtendClicked} iconClass='icons8-reset' label='Extend Session' />
      </Modal.Footer>
    </Modal>
  );
}